import { FlatTreeControl } from '@angular/cdk/tree';
import {
  Component,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable, takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroupDirective,
  NgForm,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormField, MatFormField as MatFormField_1 } from '@angular/material/form-field';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTree,
  MatTreeNodeDef,
  MatTreeNode,
  MatTreeNodePadding,
  MatTreeNodeToggle,
} from '@angular/material/tree';
import {
  ExpandableOptions,
  ExpandableResource,
  getParentNode,
  makeNewFolderFactory,
  makeTransientFolder,
  treeNodesReplacer,
} from '@miq/utils';
import { NgClass } from '@angular/common';
import { L10nTranslationModule } from 'angular-l10n';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { DataCellFormatterPipe } from '../../pipes/data-cell-formatter.pipe';

/**
 * L2-S1/T7: moved from `src/app/shared/components/create-select-folders/create-select-folders.component.ts`,
 * where it was a sanctioned reports-remote seam (the remote's `create-folder-dialog` renders it).
 *
 * It had ZERO blockers: every dependency already lived in a library or moved with it — the folder
 * factories and `ExpandableResource` were already in @miq/utils, `DataCellFormatterPipe` already in
 * this library (L2-R7), and the two mat-tree helpers (`getParentNode` / `treeNodesReplacer`) went
 * to @miq/utils/tree in the same pass. The old host path is a re-export shim.
 */
const getLevel = (node: ExpandableResource<any>) => node.level;
const isExpandable = (node: ExpandableResource<any>) => node.isContainer;
const getChildren = (node: ExpandableResource<any>): ExpandableResource<any>[] => node.children || [];
export class EmptyFolderName implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-create-select-folders',
  templateUrl: './create-select-folders.component.html',
  styleUrl: './create-select-folders.component.scss',
  imports: [
    L10nTranslationModule,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodePadding,
    MatIconButton,
    MatIcon,
    MatFormField_1,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatTreeNodeToggle,
    NgClass,
    DataCellFormatterPipe
],
  host: {
    '(document:click)': 'clickout($event)',
  },
})
export class CreateSelectFoldersComponent implements OnInit, OnDestroy {
  protected destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  nodes = input<ExpandableResource<any>[]>([]);
  expandableOptions = input<ExpandableOptions>();
  selectionModeOnly = input(false);
  nodeDataParser = input({ displayFn: ctx => ctx });
  enableNone = input(false);
  itemCreated = output<any>()
  itemSelected = output<any>()
  private readonly inputFolderNameRef = viewChild<MatFormField>('inputFolderNameRef');
  treeControl: FlatTreeControl<ExpandableResource<any>>;
  dataSource: MatTreeFlatDataSource<ExpandableResource<any>, ExpandableResource<any>>;
  folderFormControl = new FormControl('', []);
  noNameMatcher = new EmptyFolderName();
  private readonly treeFlattener: MatTreeFlattener<ExpandableResource<any>, ExpandableResource<any>>;
  private readonly nestedNodeMap = new Map<ExpandableResource<any>, ExpandableResource<any>>();
  private readonly dataChange$ = signal<ExpandableResource<any>[]>([]);
  /**
   * The expanded elements are being resseted when datasource is updated, so adding this
   * to keep the reference o them and recover after datasource is updated
   * */
  private expandedElements = [];
  hasNoContent = input(() => false);
  constructor() {
    this.treeFlattener = new MatTreeFlattener(this.transformer.bind(this), getLevel, isExpandable, getChildren);
    this.treeControl = new FlatTreeControl<ExpandableResource<any>>(getLevel, isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    effect(() => {
      const nodes = this.nodes();
      if (nodes) {
        this.updateNodes();
      }
    });
  }
  ngOnInit() {
    toObservable(this.dataChange$, { injector: this.injector }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(nodes => {
      this.dataSource.data = nodes;
      this.treeControl.dataNodes
        .filter((folder: any) => this.expandedElements.some(({ id: expandedId }) => expandedId === folder.id))
        .forEach((node: any) => {
          node.expanded = true;
          this.treeControl.expand(node);
        });
    });
  }
  ngOnDestroy() {
    /** Required by untilDestroy */
  }
  transformer = (node: ExpandableResource<any>, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode === node ? existingNode : new ExpandableResource(node, this.expandableOptions());
    flatNode.level = level;
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };
  updateNodes() {
    const createOrSelectNone = this.selectionModeOnly() ? makeTransientFolder() : makeNewFolderFactory();
    return this.dataChange$.set([createOrSelectNone, ...this.nodes()]);
  }
  toggleNode(node) {
    node.toggle();
    this.expandedElements = node.expanded
      ? [...this.expandedElements, node]
      : this.expandedElements.filter(folder => folder.id !== node.id);
  }
  addNewItem(node: any) {
    const newFolder = makeNewFolderFactory();
    const level = this.treeControl.getLevel(node);
    newFolder.level = level;
    if (!node.children || node.children.every(({ id }) => !!id)) {
      node.insertChild(newFolder);
      const currentDs = this.dataChange$();
      this.dataChange$.set(treeNodesReplacer(currentDs, node));
    }
  }
  /** Builds the name and makes the call to create, we add | any type to avoid TS lint errors
   * due to ExpandableResource missing attributes it does not recognize since a ExpandableResource is agnostic to
   * other fields than expandable, id, expanded
   */
  saveNode(node: ExpandableResource<any> | any) {
    const parentNode = getParentNode(this.treeControl, node);
    this.folderFormControl.setValidators([Validators.required, Validators.minLength(1)]);
    const value = this.folderFormControl.value.trim();
    if (!value) {
      this.folderFormControl.markAsDirty();
      this.folderFormControl.setErrors([{ empty: true }]);
      this.folderFormControl.setValue(value);
      return false;
    }
    node.name = `${parentNode ? `${parentNode.name}/` : ''}${encodeURIComponent(value)}`;
    this.itemCreated.emit({ node, parentNode, value });
    this.resetFormControl();
  }
  updateSelection(item: ExpandableResource<any>) {
    // selected does not exist as base attribute in ExpandableResource however, is added here on the fly
    // due specific functionality that occurs only in this component. (can be added if used in any other place)
    this.treeControl.dataNodes.forEach((folder: any) => (folder.selected = folder.id === item.id));
  }
  clearIfEmpty() {
    const value = this.folderFormControl.value.trim();
    if (!value) {
      this.resetFormControl();
    }
  }
  resetFormControl() {
    this.folderFormControl.markAsPristine();
    this.folderFormControl.setValue('');
    this.folderFormControl.setErrors([]);
    this.folderFormControl.markAsUntouched();
  }
  clickout(event) {
    if (this.clickedOutside(event)) {
      this.resetFormControl();
    }
  }
  private clickedOutside(event) {
    return this.inputFolderNameRef() && !this.inputFolderNameRef()._elementRef.nativeElement.contains(event.target);
  }
}
